import { CartErrorEvent, CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const NATIVE_BUNDLE_ACTIVE_CLASS = 'packfreund-native-bundle-active';

/**
 * Theme-native Packfreund bundle selector.
 * Adds every selected color variant in one Shopify Ajax Cart request.
 */
class PackfreundBundleOffers extends HTMLElement {
  #controller = new AbortController();
  #submitting = false;
  #statusTimeout;

  connectedCallback() {
    this.#controller.abort();
    this.#controller = new AbortController();
    document.documentElement.classList.add(NATIVE_BUNDLE_ACTIVE_CLASS);

    const { signal } = this.#controller;
    this.addEventListener('change', this.#handleChange, { signal });
    this.addEventListener('click', this.#handleClick, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, { signal });
    this.#syncSelectedOffer();
  }

  disconnectedCallback() {
    this.#controller.abort();
    if (this.#statusTimeout) clearTimeout(this.#statusTimeout);

    queueMicrotask(() => {
      if (!document.querySelector('packfreund-bundle-offers')) {
        document.documentElement.classList.remove(NATIVE_BUNDLE_ACTIVE_CLASS);
      }
    });
  }

  #handleCartUpdate = (event) => {
    if (event.target === this && event.action === 'add') return;
    this.#setStatus('');
  };

  #setStatus(message, state = '', autoClearDelay = 0) {
    const status = this.querySelector('[data-packfreund-bundle-status]');
    if (!(status instanceof HTMLElement)) return;

    if (this.#statusTimeout) clearTimeout(this.#statusTimeout);
    this.#statusTimeout = undefined;
    status.textContent = message;

    if (state) {
      status.dataset.state = state;
    } else {
      delete status.dataset.state;
    }

    if (message && autoClearDelay > 0) {
      this.#statusTimeout = setTimeout(() => this.#setStatus(''), autoClearDelay);
    }
  }

  #handleChange = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (!event.target.matches('[data-packfreund-bundle-radio]')) return;
    this.#syncSelectedOffer();
  };

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const swatch = event.target.closest('[data-packfreund-bundle-swatch]');
    if (swatch instanceof HTMLButtonElement) {
      event.preventDefault();
      this.#selectSwatch(swatch);
      return;
    }

    const submit = event.target.closest('[data-packfreund-bundle-submit]');
    if (submit instanceof HTMLButtonElement) {
      event.preventDefault();
      void this.#addSelectedBundle();
    }
  };

  #selectSwatch(swatch) {
    if (swatch.disabled) return;

    const row = swatch.closest('[data-packfreund-bundle-row]');
    if (!(row instanceof HTMLElement)) return;

    for (const option of row.querySelectorAll('[data-packfreund-bundle-swatch]')) {
      if (!(option instanceof HTMLButtonElement)) continue;

      const isSelected = option === swatch;
      option.classList.toggle('is-selected', isSelected);
      option.setAttribute('aria-pressed', String(isSelected));
    }

    row.dataset.selectedVariantId = swatch.dataset.variantId || '';
    row.dataset.selectedOption = swatch.dataset.optionValue || '';
  }

  #syncSelectedOffer() {
    const selectedRadio = this.querySelector('[data-packfreund-bundle-radio]:checked');
    if (!(selectedRadio instanceof HTMLInputElement)) return;

    let selectedCard;
    for (const card of this.querySelectorAll('[data-packfreund-bundle-tier]')) {
      if (!(card instanceof HTMLElement)) continue;

      const radio = card.querySelector('[data-packfreund-bundle-radio]');
      const isSelected = radio === selectedRadio;
      card.dataset.selected = String(isSelected);
      card.setAttribute('aria-current', isSelected ? 'true' : 'false');
      if (isSelected) selectedCard = card;
    }

    if (!(selectedCard instanceof HTMLElement)) return;

    const currentPrice = this.querySelector('[data-packfreund-bundle-cta-current]');
    const comparePrice = this.querySelector('[data-packfreund-bundle-cta-compare]');

    if (currentPrice instanceof HTMLElement) {
      currentPrice.textContent = selectedCard.dataset.offerPriceText || '';
    }

    if (comparePrice instanceof HTMLElement) {
      const compareText = selectedCard.dataset.comparePriceText || '';
      comparePrice.textContent = compareText;
      comparePrice.hidden = !compareText;
    }

    this.dataset.selectedQuantity = selectedCard.dataset.quantity || '1';
  }

  #getSelectedCard() {
    const selectedRadio = this.querySelector('[data-packfreund-bundle-radio]:checked');
    return selectedRadio?.closest('[data-packfreund-bundle-tier]');
  }

  #getSelectedItems(card) {
    const quantities = new Map();

    for (const row of card.querySelectorAll('[data-packfreund-bundle-row]')) {
      if (!(row instanceof HTMLElement)) continue;

      const variantId = row.dataset.selectedVariantId;
      if (!variantId) continue;
      quantities.set(variantId, (quantities.get(variantId) || 0) + 1);
    }

    return Array.from(quantities, ([id, quantity]) => ({ id: Number(id), quantity }));
  }

  async #refreshCart() {
    const cartItemsComponent = document.querySelector('cart-items-component');
    if (cartItemsComponent && 'fetchCartData' in cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      return cartItemsComponent.fetchCartData();
    }

    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });

    if (!response.ok) throw new Error(`Warenkorb konnte nicht geladen werden (${response.status}).`);
    return response.json();
  }

  async #addSelectedBundle() {
    if (this.#submitting) return;

    const card = this.#getSelectedCard();
    const submit = this.querySelector('[data-packfreund-bundle-submit]');
    if (!(card instanceof HTMLElement) || !(submit instanceof HTMLButtonElement)) return;

    const requestedQuantity = Number(card.dataset.quantity || 0);
    const items = this.#getSelectedItems(card);
    const itemCount = items.reduce((total, item) => total + item.quantity, 0);

    if (!items.length || itemCount !== requestedQuantity) {
      this.#setStatus('Bitte wähle für jeden Artikel eine verfügbare Farbe.', 'error');
      return;
    }

    this.#submitting = true;
    this.dataset.cartLoading = 'true';
    submit.disabled = true;
    submit.setAttribute('aria-busy', 'true');
    this.#setStatus('');

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((element) => element instanceof HTMLElement && element.dataset.sectionId)
      .filter(Boolean);
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferredEventPromise.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items,
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const responseData = await response.json();

      if (!response.ok || responseData.status) {
        throw new Error(responseData.description || responseData.message || 'Das Paket konnte nicht hinzugefügt werden.');
      }

      const cart = await this.#refreshCart();
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          itemCount,
          items: cart.items,
          productId: this.dataset.productId,
          sections: responseData.sections,
          source: 'packfreund-bundle-offers',
          sourceId: this.id,
        },
      });

      this.dataset.added = 'true';
      this.#setStatus('Das ausgewählte Paket wurde in den Warenkorb gelegt.', 'success', 3500);
      setTimeout(() => delete this.dataset.added, 1200);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Das Paket konnte nicht hinzugefügt werden.';
      deferredEventPromise.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: message,
          code: 'INVALID',
        })
      );
      this.#setStatus(message, 'error');
    } finally {
      this.#submitting = false;
      delete this.dataset.cartLoading;
      submit.disabled = false;
      submit.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('packfreund-bundle-offers')) {
  customElements.define('packfreund-bundle-offers', PackfreundBundleOffers);
}
